import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Edit, Trash2, Plus, Upload, X, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import api from '../../utils/api';

interface Service {
  id: number;
  name: string;
  description: string | null;
  base_price: number | null;
  estimated_duration: number | null;
  estimated_duration_unite: 'hours' | 'days' | 'weeks' | null;
  is_active: boolean;
  icon: string | File | null;
}

type SortOrder = 'asc' | 'desc' | null;

interface LazyParams {
  first: number;
  rows: number;
  page: number;
  sortField: string | null;
  sortOrder: SortOrder;
  filters: Record<string, any>;
}

export default function Services() {
  const [services, setServices] = useState<Service[]>([]);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [lazyParams, setLazyParams] = useState<LazyParams>({
    first: 0,
    rows: 15,
    page: 1,
    sortField: null,
    sortOrder: null,
    filters: {}
  });
  const { t } = useTranslation();
  const [toast, setToast] = useState({ visible: false, severity: '', message: '' });
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchServices();
  }, [lazyParams]);

  const durationUnitOptions = [
    { label: t('services_duration_unit_hours'), value: 'hours' },
    { label: t('services_duration_unit_days'), value: 'days' },
    { label: t('services_duration_unit_weeks'), value: 'weeks' },
  ];

  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await api.get('/services', {
        params: {
          page: lazyParams.page,
          per_page: lazyParams.rows,
          sort_field: lazyParams.sortField || undefined,
          sort_order: lazyParams.sortOrder || undefined,
          ...lazyParams.filters
        }
      });
      setServices(response.data.data);
      setTotalRecords(response.data.total);
    } catch (error) {
      console.error('Error fetching services:', error);
      showToast('error', t('services_error_fetching'));
    } finally {
      setLoading(false);
    }
  };

  const showToast = (severity: string, message: string) => {
    setToast({ visible: true, severity, message });
    setTimeout(() => setToast({ ...toast, visible: false }), 3000);
  };

  const openNew = () => {
    setEditingService({
      id: 0,
      name: '',
      description: '',
      base_price: null,
      estimated_duration: null,
      estimated_duration_unite: null,
      is_active: true,
      icon: null,
    });
    setDialogVisible(true);
  };

  const hideDialog = () => {
    setDialogVisible(false);
    setEditingService(null);
  };

  const saveService = async () => {
    if (!editingService) return;

    try {
      const formData = new FormData();
      Object.entries(editingService).forEach(([key, value]) => {
        if (value !== null && value !== undefined && key !== 'icon') {
          formData.append(key, key === 'is_active' ? (value ? '1' : '0') : value.toString());
        }
      });

      const iconFile = fileInputRef.current?.files?.[0];
      if (iconFile) {
        formData.append('icon', iconFile);
      }

      if (editingService.id === 0) {
        await api.post('/services', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        showToast('success', t('services_created'));
      } else {
        formData.append('_method', 'PUT');
        await api.post(`/services/${editingService.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        showToast('success', t('services_updated'));
      }
      fetchServices();
      hideDialog();
    } catch (error) {
      console.error('Error saving service:', error);
      showToast('error', t('services_error_saving'));
    }
  };

  const editService = (service: Service) => {
    setEditingService({ ...service });
    setDialogVisible(true);
  };

  const confirmDeleteService = (service: Service) => {
    if (window.confirm(t('services_confirm_delete'))) {
      deleteService(service);
    }
  };

  const deleteService = async (service: Service) => {
    try {
      await api.delete(`/services/${service.id}`);
      showToast('success', t('services_deleted'));
      fetchServices();
    } catch (error) {
      console.error('Error deleting service:', error);
      showToast('error', t('services_error_deleting'));
    }
  };

  const onPage = (page: number) => {
    setLazyParams(prevState => ({ ...prevState, page, first: page * lazyParams.rows }));
  };

  const onSort = (field: string) => {
    const sortOrder = lazyParams.sortField === field && lazyParams.sortOrder === 'asc' ? 'desc' : 'asc';
    setLazyParams(prevState => ({ ...prevState, sortField: field, sortOrder }));
  };

  const onFilter = (field: string, value: string) => {
    setLazyParams(prevState => ({
      ...prevState,
      first: 0,
      page: 1,
      filters: { ...prevState.filters, [field]: value }
    }));
  };

  const handleIconUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && editingService) {
      setEditingService({ ...editingService, icon: file });
    }
  };

  return (
    <div className="p-6 min-h-screen bg-gray-100">
      {toast.visible && (
        <div className={`fixed top-4 right-4 p-4 rounded-md shadow-md ${
          toast.severity === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white`}>
          {toast.message}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-800">{t('services_manage')}</h2>
          <button
            onClick={openNew}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition duration-200"
          >
            <Plus className="inline-block mr-2" size={18} />
            {t('services_new')}
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('services_icon')}
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => onSort('name')}
                >
                  {t('services_name')}
                  {lazyParams.sortField === 'name' && (
                    <span className="ml-2">
                      {lazyParams.sortOrder === 'asc' ? '▲' : '▼'}
                    </span>
                  )}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('services_description')}
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => onSort('base_price')}
                >
                  {t('services_base_price')}
                  {lazyParams.sortField === 'base_price' && (
                    <span className="ml-2">
                      {lazyParams.sortOrder === 'asc' ? '▲' : '▼'}
                    </span>
                  )}
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => onSort('estimated_duration')}
                >
                  {t('services_duration')}
                  {lazyParams.sortField === 'estimated_duration' && (
                    <span className="ml-2">
                      {lazyParams.sortOrder === 'asc' ? '▲' : '▼'}
                    </span>
                  )}
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => onSort('is_active')}
                >
                  {t('services_status')}
                  {lazyParams.sortField === 'is_active' && (
                    <span className="ml-2">
                      {lazyParams.sortOrder === 'asc' ? '▲' : '▼'}
                    </span>
                  )}
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center">{t('loading')}</td>
                </tr>
              ) : services.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center">{t('services_no_services_found')}</td>
                </tr>
              ) : (
                services.map((service) => (
                  <tr key={service.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      {service.icon && (
                        <img 
                          src={`${process.env.REACT_APP_PIC_STORAGE}/${service.icon}`} 
                          alt={service.name} 
                          className="w-10 h-10 object-contain rounded-full bg-gray-100 p-1 border border-gray-200"
                        />
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{service.name}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500">
                        {service.description && service.description.length > 50 
                          ? `${service.description.substring(0, 50)}...` 
                          : service.description}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {service.base_price ? `${service.base_price} MAD` : ''}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {service.estimated_duration && service.estimated_duration_unite
                        ? `${service.estimated_duration} ${t(`services_duration_unit_${service.estimated_duration_unite}`)}`
                        : ''}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        service.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {service.is_active ? t('active') : t('inactive')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => editService(service)}
                        className="text-indigo-600 hover:text-indigo-900 mr-2"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => confirmDeleteService(service)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
          <div>
            <p className="text-sm text-gray-700">
              {t('showing')} <span className="font-medium">{lazyParams.first + 1}</span> {t('to')} <span className="font-medium">{Math.min(lazyParams.first + lazyParams.rows, totalRecords)}</span> {t('of')}{' '}
              <span className="font-medium">{totalRecords}</span><span className="font-medium">{totalRecords}</span> {t('results')}
            </p>
          </div>
          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
              <button
                onClick={() => onPage(1)}
                disabled={lazyParams.page === 1}
                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
              >
                <span className="sr-only">{t('first')}</span>
                <ChevronsLeft className="h-5 w-5" aria-hidden="true" />
              </button>
              <button
                onClick={() => onPage(lazyParams.page - 1)}
                disabled={lazyParams.page === 1}
                className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
              >
                <span className="sr-only">{t('previous')}</span>
                <ChevronLeft className="h-5 w-5" aria-hidden="true" />
              </button>
              <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                {t('page')} {lazyParams.page} {t('of')} {Math.ceil(totalRecords / lazyParams.rows)}
              </span>
              <button
                onClick={() => onPage(lazyParams.page + 1)}
                disabled={lazyParams.page === Math.ceil(totalRecords / lazyParams.rows)}
                className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
              >
                <span className="sr-only">{t('next')}</span>
                <ChevronRight className="h-5 w-5" aria-hidden="true" />
              </button>
              <button
                onClick={() => onPage(Math.ceil(totalRecords / lazyParams.rows))}
                disabled={lazyParams.page === Math.ceil(totalRecords / lazyParams.rows)}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
              >
                <span className="sr-only">{t('last')}</span>
                <ChevronsRight className="h-5 w-5" aria-hidden="true" />
              </button>
            </nav>
          </div>
        </div>
      </div>

      {dialogVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-semibold">{editingService && editingService.id ? t('services_edit') : t('services_new')}</h2>
              <button onClick={hideDialog} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); saveService(); }} className="p-6">
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">{t('services_name')}</label>
                  <input
                    type="text"
                    id="name"
                    value={editingService?.name || ''}
                    onChange={(e) => setEditingService(prev => prev ? { ...prev, name: e.target.value } : null)}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700">{t('services_description')}</label>
                  <textarea
                    id="description"
                    value={editingService?.description || ''}
                    onChange={(e) => setEditingService(prev => prev ? { ...prev, description: e.target.value } : null)}
                    rows={3}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="base_price" className="block text-sm font-medium text-gray-700">{t('services_base_price')}</label>
                  <input
                    type="number"
                    id="base_price"
                    value={editingService?.base_price ?? ''} 
                    onChange={(e) =>
                      setEditingService((prev) =>
                        prev
                          ? { ...prev, base_price: e.target.value === '' ? null : parseFloat(e.target.value) }
                          : null
                      )
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  min="0"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="estimated_duration" className="block text-sm font-medium text-gray-700">{t('services_estimated_duration')}</label>
                    <input
                      type="number"
                      id="estimated_duration"
                      value={editingService?.estimated_duration || ''}
                      onChange={(e) => setEditingService(prev => prev ? { ...prev, estimated_duration: parseInt(e.target.value) || null } : null)}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="estimated_duration_unite" className="block text-sm font-medium text-gray-700">{t('services_estimate_duration_unit')}</label>
                    <select
                      id="estimated_duration_unite"
                      value={editingService?.estimated_duration_unite || ''}
                      onChange={(e) => setEditingService(prev => prev ? { ...prev, estimated_duration_unite: e.target.value as 'hours' | 'days' | 'weeks' | null } : null)}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">{t('services_select_duration_unit')}</option>
                      {durationUnitOptions.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label htmlFor="icon" className="block text-sm font-medium text-gray-700">{t('services_icon_and_status')}</label>
                  <div className="flex items-center space-x-4 mt-1">
                    {editingService?.icon && (
                      <img 
                        src={editingService.icon instanceof File ? URL.createObjectURL(editingService.icon) : `${process.env.REACT_APP_PIC_STORAGE}/${editingService.icon}`} 
                        alt="Service Icon" 
                        className="w-16 h-16 object-contain rounded-full bg-gray-100 p-1 border border-gray-200" 
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="px-4 py-2 border rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <Upload className="w-5 h-5 inline-block mr-2" />
                      {t('services_upload_icon')}
                    </button>
                    <input
                      type="file"
                      ref={fileInputRef}
                      hidden
                      accept="image/*"
                      onChange={handleIconUpload}
                    />
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="is_active"
                        checked={editingService?.is_active || false}
                        onChange={(e) => setEditingService(prev => prev ? { ...prev, is_active: e.target.checked } : null)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500border p-2rounded"
                      />
                      <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                        {editingService?.is_active ? t('active') : t('inactive')}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={hideDialog}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {t('save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

